import React from 'react';
import {
  BuildingOfficeIcon,
  GlobeIcon,
  BadgeIcon,
  StarIcon,
  MailIcon,
  UsersIcon,
} from '../icons';
import { normalizeDestination } from '../../utils/destinations';

const CompanyProfile = ({ currentUser, onNotify, onRequestAdminNotification }) => {
  const handleRequestDelegate = async () => {
    try {
      await onRequestAdminNotification?.({
        type: 'delegate_request',
        title: 'Solicitud de delegado',
        message: `${currentUser.name} solicita apoyo para registrar un delegado temporal o coordinar el envío del cuestionario.`,
        company: currentUser,
        targetPage: 'admin_users',
      });
      onNotify('Se ha notificado al administrador sobre tu solicitud de delegado.', 'info');
    } catch (error) {
      onNotify(error.message || 'No se pudo notificar al administrador.', 'error');
    }
  };

  const handleRequestExtension = async () => {
    try {
      await onRequestAdminNotification?.({
        type: 'extension_request',
        title: 'Solicitud de prórroga',
        message: `${currentUser.name} solicita una extensión para completar el cuestionario del período actual.`,
        company: currentUser,
        targetPage: 'admin_deadlines',
      });
      onNotify('Se ha notificado al administrador sobre tu solicitud de prórroga.', 'info');
    } catch (error) {
      onNotify(error.message || 'No se pudo notificar al administrador.', 'error');
    }
  };

  const statCards = [
    { label: 'Sector de exportación', value: currentUser.exportType || 'No especificado', icon: BuildingOfficeIcon },
    { label: 'Destino principal', value: normalizeDestination(currentUser.destination), icon: GlobeIcon },
    { label: 'Identificador', value: currentUser.id, icon: BadgeIcon },
  ];

  return (
    <div className="p-4 md:p-8 bg-[#F6F8FB] min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[#004AAD] to-[#3B82F6]" />
          <div className="p-6 md:p-8 space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <p className="text-[0.6rem] uppercase text-slate-400">Perfil corporativo</p>
                <h1 className="text-3xl font-bold mt-1 text-slate-900">{currentUser.name}</h1>
                <p className="text-slate-500 mt-1">Participante del Reporte Naviero</p>
              </div>
              <div className="text-right">
                <p className="text-[0.6rem] uppercase text-slate-400">Nivel</p>
                <p className="text-4xl font-bold flex items-center justify-end gap-2 text-[#004AAD]">
                  {currentUser.stars}
                  <StarIcon solid className="h-7 w-7 text-amber-400" />
                </p>
                <p className="text-sm text-slate-500">estrellas acumuladas</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {statCards.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-xl bg-white border border-slate-200 text-[#004AAD]">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="uppercase text-slate-400 text-[0.6rem]">{stat.label}</p>
                        <p className="text-base font-semibold text-slate-900">{stat.value}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-xl bg-white border border-slate-200/70 shadow-sm p-6 space-y-4">
            <p className="text-[0.6rem] uppercase text-slate-400">Contacto</p>
            <div className="flex items-center gap-3">
              <span className="p-3 rounded-xl bg-slate-100">
                <MailIcon className="h-5 w-5 text-slate-600" />
              </span>
              <div>
                <p className="text-sm text-slate-500">Correo principal</p>
                <p className="text-lg font-semibold text-slate-900">{currentUser.contactEmail ? <a href={`mailto:${currentUser.contactEmail}`} className="text-[#004AAD]">{currentUser.contactEmail}</a> : 'Sin registrar'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="p-3 rounded-xl bg-slate-100">
                <UsersIcon className="h-5 w-5 text-slate-600" />
              </span>
              <div>
                <p className="text-sm text-slate-500">Responsable del cuestionario</p>
                <p className="text-lg font-semibold text-slate-900">{currentUser.respondent || 'No asignado'}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white border border-slate-200/70 shadow-sm p-6 space-y-4">
            <p className="text-[0.6rem] uppercase text-slate-400">Participación</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Último envío registrado</p>
                <p className="font-semibold text-slate-900">{currentUser.lastSubmission || 'En preparación'}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Destino prioritario</p>
                <p className="font-semibold text-slate-900">{normalizeDestination(currentUser.destination)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Sector</p>
                <p className="font-semibold text-slate-900">{currentUser.exportType}</p>
              </div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              <p>Promueve que tu equipo participe cada mes para mantener activo el pulso sectorial y desbloquear reconocimientos.</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-slate-200/70 shadow-sm p-6 space-y-4">
          <p className="text-[0.6rem] uppercase text-slate-400">Delegación</p>
          <h2 className="text-2xl font-semibold text-slate-900">Solicita apoyo cuando no puedas enviar el cuestionario</h2>
          <p className="text-slate-600">Enviaremos una alerta al equipo administrador para que registre un delegado temporal o gestione una prórroga para tu período.</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={handleRequestDelegate}
              className="w-full rounded-xl px-6 py-3 text-white font-semibold transition bg-[#004AAD] hover:bg-[#003B8A] sm:w-auto"
            >
              Solicitar delegado
            </button>
            <button
              onClick={handleRequestExtension}
              className="w-full rounded-xl border border-slate-200 px-6 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 sm:w-auto"
            >
              Solicitar prórroga
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
